import { Box, BoxProps, Typography } from '@mui/material'
import { FunctionComponent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  sparklinePoints, Status, STATUS_STYLE, trend, TREND_COLOR
} from '../status'

// design tokens of the dashboard v2 draft
export const TOKENS = {
  ground: '#DCE1E9',
  surface: '#FFFFFF',
  border: '#C3CCD8',
  // lifts the white cards off the light page background
  cardShadow: '0 1px 3px rgba(16, 24, 40, 0.14), 0 2px 8px rgba(16, 24, 40, 0.07)',
  // visible on bright office monitors too (was #EEF1F5)
  divider: '#E0E5EC',
  text: '#142033',
  muted: '#4B5565',
  primary: '#0063C4',
  primarySoft: '#E6F0FB',
  primaryDark: '#0050A0'
}

export const DashCard: FunctionComponent<Omit<BoxProps, 'title'> & { title?: ReactNode, subtitle?: ReactNode, action?: ReactNode }> = ({
  title, subtitle, action, children, sx, ...rest
}) => (
  <Box
    component="section"
    sx={{
      bgcolor: TOKENS.surface,
      border: `1px solid ${TOKENS.border}`,
      boxShadow: TOKENS.cardShadow,
      borderRadius: '12px',
      p: { xs: 2, md: 2.5 },
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      ...sx
    }}
    {...rest}
  >
    {(title || action) && (
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap'
      }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 650, color: TOKENS.text, m: 0 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 13, color: TOKENS.muted, mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
)

export const StatusChip: FunctionComponent<{ status: Status, short?: boolean }> = ({ status }) => {
  const { t } = useTranslation()
  const style = STATUS_STYLE[status]
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: 24,
        px: 1.1,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 650,
        whiteSpace: 'nowrap',
        bgcolor: style.bg,
        color: style.fg
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={style.icon} />
      </svg>
      {t(`dashboardV2.status.${status}`)}
    </Box>
  )
}

export const Sparkline: FunctionComponent<{ values: (number | null)[], width: number, height: number, color?: string, max?: number }> = ({
  values, width, height, color = TOKENS.primary, max
}) => (
  <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" style={{ flex: 'none' }}>
    <polyline
      points={sparklinePoints(values, width, height, max)}
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </svg>
)

// "6 Pkt. weniger", "3 mehr", ... coloured by whether the change is good
export const Delta: FunctionComponent<{
  value: number | null, previous: number | null, percent?: boolean, higherIsBetter?: boolean
}> = ({
  value, previous, percent = false, higherIsBetter = true
}) => {
  const { t } = useTranslation()
  const direction = trend(value, previous, higherIsBetter)
  let text: string
  if (direction === 'none') text = t('dashboardV2.kpi.noComparison')
  else if (direction === 'same') text = t('dashboardV2.kpi.unchanged')
  else {
    const diff = Math.abs((value as number) - (previous as number))
    const up = (value as number) > (previous as number)
    const key = percent ? (up ? 'pointsMore' : 'pointsLess') : (up ? 'more' : 'less')
    text = t(`dashboardV2.kpi.${key}`, { count: diff })
  }
  return (
    <Typography component="span" sx={{ fontSize: 13, fontWeight: 650, color: TREND_COLOR[direction], fontVariantNumeric: 'tabular-nums' }}>
      {text}
    </Typography>
  )
}

export const Muted: FunctionComponent<{ children: ReactNode, size?: number }> = ({ children, size = 13 }) => (
  <Typography component="span" sx={{ fontSize: size, color: TOKENS.muted }}>{children}</Typography>
)

export function daysSince(date: string | null, now = new Date()): number | null {
  if (!date) return null
  const start = new Date(date)
  const a = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((b - a) / 86400000)
}
